import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import type { UploadProps } from 'antd';
import { Button, Flex, Spin, Typography, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload';

import Modal from '/ui/Modal';

import filesActions from '../../../actions';

import AiIcon from '../../../../../components/AiIcon';

import { AppDispatch } from '../../../../../types/app';

const { Dragger } = Upload;
const { Title, Text } = Typography;

const CSV_EXT = /\.csv$/i;

const getDisplayName = (file: File): string => {
  const base = file.name.replace(CSV_EXT, '') || file.name;
  return base.slice(0, 255);
};

type Props = {
  visible: boolean;
  onClose: () => void;
  fileId?: number;
};

const UploadFileModal = ({ visible, onClose, fileId }: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadFile = useCallback(
    async (file: RcFile) => {
      if (uploading) return;

      if (!CSV_EXT.test(file.name)) {
        setError(intl.formatMessage({ id: 'files.uploadModal.errorNotCsv' }));
        return;
      }

      setUploading(true);
      setError(null);

      try {
        if (fileId != null) {
          await dispatch(filesActions.Files.uploadFile(fileId, file));
        } else {
          await dispatch(
            filesActions.Files.addFile(getDisplayName(file), file)
          );
        }
        onClose();
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : intl.formatMessage({ id: 'files.uploadModal.errorUpload' });

        console.error(err);
        setError(message);
      } finally {
        setUploading(false);
      }
    },
    [dispatch, fileId, intl, onClose, uploading]
  );

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept: '.csv',
    showUploadList: false,
    disabled: uploading,
    beforeUpload: (file) => {
      void uploadFile(file);
      return false;
    },
  };
  const draggerProps: UploadProps = {
    ...uploadProps,
    openFileDialogOnClick: false,
  };

  return (
    <Modal
      className="files-page__upload-modal"
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnHidden
      width={604}
      closable={false}
      mask={{
        closable: !uploading,
      }}
      styles={{
        mask: { background: 'rgba(2, 4, 9, 0.8)' },
        body: {
          padding: 0,
          background: '#121826',
          borderRadius: 20,
          border: 0,
          outline: 0,
          boxShadow: 'none',
        },
      }}
    >
      <Spin spinning={uploading}>
        <div className="files-page__upload-modal-inner">
          <Dragger {...draggerProps} className="files-page__upload-dropzone">
            <Flex className="files-page__upload-actions" gap={20}>
              <Upload {...uploadProps}>
                <span className="files-page__upload-icon" aria-hidden>
                  <AiIcon name="upload" />
                </span>
                <Title level={4} className="files-page__upload-title">
                  <FormattedMessage id="files.uploadModal.title" />
                </Title>
                <Text className="files-page__upload-hint">
                  <FormattedMessage id="files.uploadModal.hint" />
                </Text>
                <Flex gap={20} style={{ marginTop: 28 }}>
                  <Button
                    type="primary"
                    className="files-page__upload-import-btn"
                    loading={uploading}
                    disabled={uploading}
                  >
                    <FormattedMessage id="files.uploadModal.import" />
                  </Button>
                  <Button
                    className="files-page__upload-cancel-btn"
                    onClick={(event) => {
                      event.stopPropagation();
                      onClose();
                    }}
                    disabled={uploading}
                  >
                    <FormattedMessage id="files.uploadModal.cancel" />
                  </Button>
                </Flex>
              </Upload>
            </Flex>
          </Dragger>

          {error && (
            <Text type="danger" className="files-page__upload-error">
              {error}
            </Text>
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default UploadFileModal;
